import $ from "jquery";
import Snap from "snapsvg-cjs-ts";
import { POINT_PRESETS } from "../shared/PointPresets";
import { MapView } from "./Basemap";
import { ColorPicker, InputNumber, OnOffButton, Selectbox } from "./Form";
import { readColor, readFlag } from "./UrlState";

type Position = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export class Points {
    static readonly data: Map<string, [string, number, number]> = new Map();
    static async fetch() {
        const data = await fetch("points.json").then((res) => res.json());
        for (const [id, row] of Object.entries(data)) {
            const [name, xstr, ystr] = row as [string, string, string];
            this.data.set(id, [name, Number(xstr), Number(ystr)]);
        }
    }

    private readonly onOffButton = new OnOffButton("points");
    private readonly textSize = new InputNumber("#input_Pts", {
        min: 0,
        step: 3,
    });
    private readonly radius = new InputNumber("#input_Pr", {
        min: 0,
        step: 1,
    });
    private readonly color: ColorPicker;
    private readonly positionSelector = new Selectbox("#select_Ppos");
    private readonly amedasPicker = new AmedasPicker();
    private readonly customPicker = new CustomPicker();
    private readonly amedasPoints: AmedasPoints;
    private readonly customPoints: CustomPoints;
    constructor(
        private readonly params: URLSearchParams,
        private readonly mapView: MapView,
    ) {
        this.onOffButton.value = readFlag(this.params, "pts_on", "0");
        this.textSize.value = this.numberParam("pts_ts", 30);
        this.radius.value = this.numberParam("pts_r", 10);
        this.color = new ColorPicker(
            "#select_Pfc",
            readColor(this.params, "pts_fc", "#ffffff"),
            ["#fff", "#999", "#ff0", "#f00", "#00f"],
            { opacity: false },
        );
        this.positionSelector.value = "-1";

        this.amedasPoints = new AmedasPoints(
            this.params,
            this.mapView,
            this.radius,
            this.textSize,
            this.color,
        );
        this.customPoints = new CustomPoints(
            this.params,
            this.mapView,
            this.radius,
            this.textSize,
            this.color,
        );

        this.onOffButton.onChange(() => {
            if (
                this.onOffButton.value === "1" &&
                this.amedasPoints.isEmpty() &&
                this.customPoints.isEmpty()
            ) {
                this.amedasPoints.usePreset("J5");
            }
            this.updateDisplay();
        });
        this.textSize.onChange(() => this.updateSize());
        this.radius.onChange(() => this.updateSize());
        this.color.onChange(() => this.updateColor());
        this.positionSelector.onChange(() => this.changePosition());
        this.amedasPicker.onEnter((id) => this.addAmedas(id));
        this.customPicker.onEnter((x, y, name) => this.addCustom(x, y, name));

        for (const preset of [
            "J5",
            "J10",
            "J15",
            "J47",
            "F60",
            "F142",
            "F151",
            "W3",
            "W25",
            "W130",
            "N0",
        ]) {
            $(`#points_preset_${preset}`).on("click", () =>
                this.usePreset(preset),
            );
        }

        this.updateDisplay();
        this.updateColor();
        this.updateSize();
    }
    saveParams() {
        this.params.set("pts_on", this.onOffButton.value);
        this.params.set("pts_ts", String(this.textSize.value));
        this.params.set("pts_r", String(this.radius.value));
        this.params.set("pts_fc", this.color.getColor());
        this.amedasPoints.saveParams();
        this.customPoints.saveParams();
    }
    loadParams() {
        this.onOffButton.value = readFlag(this.params, "pts_on", "0");
        this.textSize.value = this.numberParam("pts_ts", 30);
        this.radius.value = this.numberParam("pts_r", 10);
        this.color.setColor(readColor(this.params, "pts_fc", "#ffffff"));
        this.positionSelector.value = "-1";
        this.amedasPoints.loadParams(this.params.get("pts_pts") || "");
        this.customPoints.loadParams(this.params.get("pts_custom") || "");
        this.updateDisplay();
        this.updateColor();
        this.updateSize();
    }
    private updateDisplay() {
        const display = this.onOffButton.value === "1" ? "block" : "none";
        Snap("#points_c").attr({ display });
        Snap("#points_b").attr({ display });
        Snap("#points_t").attr({ display });
    }
    private updateColor() {
        this.amedasPoints.updateColor();
        this.customPoints.updateColor();
    }
    updateSize() {
        this.amedasPoints.updateSize();
        this.customPoints.updateSize();
    }
    private changePosition() {
        if (this.positionSelector.value === "-1") {
            return;
        }
        const position = Number(this.positionSelector.value) as Position;
        this.amedasPoints.setPosition(position);
        this.customPoints.setPosition(position);
    }
    private addAmedas(id: string) {
        this.onOffButton.value = "1";
        this.updateDisplay();
        this.amedasPoints.add(id, 0);
    }
    private addCustom(x: number, y: number, name: string) {
        this.onOffButton.value = "1";
        this.updateDisplay();
        this.customPoints.add(x, y, name, 0);
    }
    private usePreset(preset: string) {
        this.onOffButton.value = "1";
        this.updateDisplay();
        this.amedasPoints.usePreset(preset);
        this.customPoints.usePreset(preset);
    }
    private numberParam(key: string, fallback: number) {
        if (!this.params.has(key)) {
            return fallback;
        }
        const value = Number(this.params.get(key));
        return Number.isFinite(value) && value >= 0 ? value : fallback;
    }
}

class AmedasPoints {
    private readonly points = new Map<string, Point>();
    constructor(
        private readonly params: URLSearchParams,
        private readonly mapView: MapView,
        private readonly radius: InputNumber,
        private readonly textSize: InputNumber,
        private readonly color: ColorPicker,
    ) {
        if (this.params.has("pts_pts") && this.params.get("pts_pts") !== "") {
            this.addByParam(this.params.get("pts_pts")!);
        }
    }
    saveParams() {
        const amedasList = [];
        for (const [id, point] of this.points) {
            const { position } = point.getParams();
            amedasList.push(position === 0 ? id : `${id}-${position}`);
        }
        this.params.set("pts_pts", amedasList.join(","));
    }
    loadParams(text: string) {
        this.clear();
        this.addByParam(text);
    }
    updateColor() {
        for (const point of this.points.values()) {
            point.updateColor();
        }
    }
    updateSize() {
        for (const point of this.points.values()) {
            point.updateSize();
        }
    }
    setPosition(position: Position) {
        for (const point of this.points.values()) {
            point.setPosition(position);
        }
    }
    isEmpty() {
        return this.points.size === 0;
    }
    add(id: string, position: Position) {
        if (this.points.has(id)) {
            return;
        }
        if (!Points.data.has(id)) {
            return;
        }
        const [name, x, y] = Points.data.get(id)!;
        const point = new Point(
            x,
            y,
            name,
            position,
            this.mapView,
            this.radius,
            this.textSize,
            this.color,
        );
        point.onRemove(() => {
            point.remove();
            this.points.delete(id);
        });
        this.points.set(id, point);
    }
    private addByParam(text: string) {
        if (text === "") {
            return;
        }
        for (const pp of text.split(",")) {
            if (pp.includes("-")) {
                const array = pp.split("-");
                this.add(array[0], Number(array[1]) as Position);
            } else {
                this.add(pp, 0);
            }
        }
    }
    private clear() {
        for (const point of this.points.values()) {
            point.remove();
        }
        this.points.clear();
    }
    usePreset(preset: string) {
        this.clear();
        if (preset === "N0") {
            return;
        }
        const presetList = POINT_PRESETS;
        this.addByParam(presetList[preset]);
    }
}

class AmedasPicker {
    private callback: (id: string) => void;
    private searchText = $("#input_Psearch");
    constructor() {
        this.searchText.on("keydown", (e) => {
            if (e.key === "Enter") {
                this.search();
            }
        });
    }
    private search() {
        let str = $("#input_Psearch").val() as string;
        if (str.length === 0) {
            return true;
        } //空っぽは何もしない
        if (
            str.length < 2 &&
            "旭鶉森岳平熊津巻泊堺針富呉境萩上奥東".indexOf(str) < 0
        ) {
            //1文字アメダスのみ許可
            $("#result_Psearch").text("2文字以上で検索してね。");
            return;
        }
        let full = 0;
        const str1 = str.slice(0, 1);
        if (str1 === "'" || str1 === '"') {
            //"か'で始まっていれば完全一致
            full = 1;
            str = str.replace(/\"/g, "").replace(/\'/g, "");
        }

        let i = 0;
        const listA: string[] = [];
        const listJ: string[] = [];
        for (const [id, [name, _x, _y]] of Points.data.entries()) {
            if (
                (name.includes(str) && full === 0) ||
                (name === str && full === 1)
            ) {
                listA[i] = id;
                listJ[i] = name;
                i++;
            }
        }
        if (i === 0) {
            $("#result_Psearch").text("見つかりませんでした。");
        } else if (i === 1) {
            $("#result_Psearch").text(`OK : ${listJ[0]}`);
            this.callback(listA[0]);
        } else {
            $("#result_Psearch").text("Select :");
            for (let j = 0; j < i; j++) {
                const a = $("<a>")
                    .text(`[${listA[j]}]${listJ[j]}`)
                    .attr("href", "javascript:void(0);")
                    .on("click", () => {
                        this.callback(listA[j]);
                    });
                $("#result_Psearch").append(" ");
                $("#result_Psearch").append(a);
            }
        }
    }
    onEnter(callback: (id: string) => void) {
        this.callback = callback;
    }
}

class CustomPoints {
    private readonly points = new Map<string, Point>();
    constructor(
        private readonly params: URLSearchParams,
        private readonly mapView: MapView,
        private readonly radius: InputNumber,
        private readonly textSize: InputNumber,
        private readonly color: ColorPicker,
    ) {
        if (
            this.params.has("pts_custom") &&
            this.params.get("pts_custom") !== ""
        ) {
            this.addByParam(this.params.get("pts_custom")!);
        }
    }
    saveParams() {
        const customList = [];
        for (const point of this.points.values()) {
            const { x, y, name, position } = point.getParams();
            customList.push(
                position === 0
                    ? `${x}_${y}_${name}`
                    : `${x}_${y}_${name}_${position}`,
            );
        }
        this.params.set("pts_custom", customList.join(","));
    }
    loadParams(text: string) {
        this.clear();
        this.addByParam(text);
    }
    updateColor() {
        for (const point of this.points.values()) {
            point.updateColor();
        }
    }
    updateSize() {
        for (const point of this.points.values()) {
            point.updateSize();
        }
    }
    setPosition(position: Position) {
        for (const point of this.points.values()) {
            point.setPosition(position);
        }
    }
    isEmpty() {
        return this.points.size === 0;
    }
    add(x: number, y: number, name: string, position: Position) {
        if (this.points.has(name)) {
            return;
        }
        const point = new Point(
            x,
            y,
            name,
            position,
            this.mapView,
            this.radius,
            this.textSize,
            this.color,
        );
        point.onRemove(() => {
            point.remove();
            this.points.delete(name);
        });
        this.points.set(name, point);
    }
    private addByParam(text: string) {
        if (text === "") {
            return;
        }
        for (const pp of text.split(",")) {
            const array = pp.split("_");
            if (array.length === 3) {
                this.add(Number(array[0]), Number(array[1]), array[2], 0);
            } else {
                this.add(
                    Number(array[0]),
                    Number(array[1]),
                    array[2],
                    Number(array[3]) as Position,
                );
            }
        }
    }
    private clear() {
        for (const point of this.points.values()) {
            point.remove();
        }
        this.points.clear();
    }
    usePreset(preset: string) {
        if (preset === "N0") {
            this.clear();
        }
    }
}

class CustomPicker {
    private callback: (x: number, y: number, name: string) => void;
    private readonly name = $("#input_Poptname");
    private readonly lon = $("#input_Poptlon");
    private readonly lat = $("#input_Poptlat");
    private readonly addButton = $("#button_Poptadd");
    constructor() {
        this.name.on("keydown", (e) => {
            if (e.key === "Enter") {
                this.fire();
            }
        });
        this.lon.on("keydown", (e) => {
            if (e.key === "Enter") {
                this.fire();
            }
        });
        this.lat.on("keydown", (e) => {
            if (e.key === "Enter") {
                this.fire();
            }
        });
        this.addButton.on("click", () => this.fire());
    }
    private fire() {
        const name = this.name.val() as string;
        const lon = Number(this.lon.val());
        const lat = Number(this.lat.val());
        if (
            name.trim() === "" ||
            !Number.isFinite(lon) ||
            !Number.isFinite(lat) ||
            lon < -180 ||
            lon > 180 ||
            lat < -90 ||
            lat > 90
        ) {
            return;
        }
        const { x, y } = lonlat_to_polar(lon, lat);
        this.callback(x, y, name.trim());
    }
    onEnter(callback: (x: number, y: number, name: string) => void) {
        this.callback = callback;
    }
}

class Point {
    private readonly textAnchor = [
        "middle",
        "start",
        "start",
        "start",
        "middle",
        "end",
        "end",
        "end",
    ];
    private readonly circle: Snap.Element;
    private readonly border: Snap.Element;
    private readonly text: Snap.Element;
    constructor(
        private readonly x: number,
        private readonly y: number,
        private readonly name: string,
        private position: Position,
        private readonly mapView: MapView,
        private readonly radius: InputNumber,
        private readonly textSize: InputNumber,
        private readonly color: ColorPicker,
    ) {
        this.circle = Snap("#points_c").circle(this.x, this.y, 0).attr({
            stroke: "#333",
            cursor: "pointer",
        });
        this.border = Snap("#points_b")
            .text(this.x, this.y, this.name)
            .attr({ stroke: "#fff", cursor: "pointer" });
        this.text = Snap("#points_t")
            .text(this.x, this.y, this.name)
            .attr({ fill: "#000", strokeWidth: 0, cursor: "pointer" })
            .mousedown((event: MouseEvent) => {
                event.preventDefault();
                const diff = event.button === 2 ? -1 : 1;
                this.position = ((this.position + diff + 8) % 8) as Position;
                this.updateSize();
            });
        this.text.node.addEventListener("contextmenu", (event) =>
            event.preventDefault(),
        );

        this.updateColor();
        this.updateSize();
    }
    setPosition(position: Position) {
        this.position = position;
        this.updateSize();
    }
    getParams() {
        return {
            x: this.x,
            y: this.y,
            name: this.name,
            position: this.position,
        };
    }
    updateColor() {
        this.circle.attr({ fill: this.color.getHex() });
    }
    updateSize() {
        const scale = this.mapView.getScreenScale();
        const r = this.radius.value * scale;
        this.circle.attr({ r, strokeWidth: 1 * scale });

        const fontSize = this.textSize.value * scale;
        const { deg } = this.mapView.getViewBox();
        const msin = Math.sin((deg / 180.0) * Math.PI);
        const mcos = Math.cos((deg / 180.0) * Math.PI);
        const cdeg = this.position * 45;
        const csin = Math.sin((cdeg / 180.0) * Math.PI);
        const ccos = Math.cos((cdeg / 180.0) * Math.PI) * -1;
        const ccosf = ccos < -0.1 ? -1 : ccos > 0.1 ? 1 : 0;
        let cx = csin * 1.5 * r;
        let cy = 0.35 * fontSize + (ccosf * fontSize) / 2 + ccos * 1.5 * r; //ベースラインずれ＋上下外側に＋半径分
        if (Math.abs(cx) < 0.00001) {
            cx = 0;
        }
        if (Math.abs(cy) < 0.00001) {
            cy = 0;
        }
        const x = cx * mcos + cy * msin;
        const y = -cx * msin + cy * mcos;
        const attr = {
            fontSize,
            textAnchor: this.textAnchor[this.position],
            transform: `translate(${x},${y}) rotate(${-deg},${this.x},${this.y})`,
        };
        this.border.attr(attr).attr({ strokeWidth: fontSize * 0.15 });
        this.text.attr(attr);
    }
    remove() {
        this.circle.remove();
        this.border.remove();
        this.text.remove();
    }
    onRemove(callback: () => void) {
        this.circle.dblclick(() => callback());
    }
}

function lonlat_to_polar(lon: number, lat: number) {
    const lam0 = (140.0 / 180) * Math.PI;
    const R = 6371.2;

    const _lon = lon < 0 ? lon + 360 : lon;
    const phi = (lat / 180) * Math.PI;
    const lam = (_lon / 180) * Math.PI;

    const K = (2 * R) / (1 + Math.sin(phi));
    const x = K * Math.cos(phi) * Math.sin(lam - lam0);
    const y = K * Math.cos(phi) * Math.cos(lam - lam0);
    return { x, y };
}
